/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { ComponentProps, ReactNode } from 'react';
import merge from 'lodash/merge';
import { useSelector } from 'react-redux';
import { TerminalDetails } from '../../../../../shared/entities/terminal';
import { TerminalPlatformsEnum } from '../../../../../shared/entities/terminalPlatform';
import {
  RootState,
  selectHasSelectedSpaceVehicleOrbit,
  selectSpaceVehicleChartOtherTerminalHistory,
  selectSpaceVehicleChartTerminalHistory,
  selectTailDetailsPanelTerminal,
} from '../../../../../store';
import { CHART_HEIGHT } from '../ChartsContainerWrapper';
import {
  ChartItemKeys,
  chartSeriesContent,
  ChartType,
  SPEED_AXIS_ID,
  spaceVehicleSpeedAndAltitudeSeriesContent,
} from '../ChartSeriesItems';
import ChartWrapper from './ChartWrapper';
import {
  defaultChartOptions,
  showChartOrDataAvailableHOC,
  withTerminalHistoryChartHOC,
  withTerminalHistoryChartHOCAndNoDataAvailableHOC,
} from './WithTerminalHistoryChartHOC';
interface SpeedAndAltitudeChartProps {
  index: number;
  text: string;
  id: string;
  chart: Highcharts.Chart | null;
  tailDetailsPanelTerminal: TerminalDetails | undefined;
  children: ReactNode | ReactNode[];
}
const SpeedAndAltitudeChart = (props: SpeedAndAltitudeChartProps) => {
  const { index, text, id, children } = props;
  const chartName = text;

  return (
    <ChartWrapper name={chartName} id={id} index={index} chartType={ChartItemKeys.speedAndAltitude}>
      {children}
    </ChartWrapper>
  );
};

const getChartOptions = (altitudeUnits: string, speedUnits: string) => {
  return merge(defaultChartOptions(ChartType.line), {
    yAxis: [
      {
        labels: {
          format: `{value} ${altitudeUnits}`,
        },
        title: {
          text: 'Altitude',
        },
      },
      {
        id: SPEED_AXIS_ID,
        labels: {
          format: `{value} ${speedUnits}`,
        },
        title: {
          text: 'Speed',
        },
        opposite: true,
      },
    ],
    plotOptions: { series: { custom: { useDetailedLabelSymbols: true } } },
  });
};

const AirborneSpeedAndAltitudeChart = withTerminalHistoryChartHOCAndNoDataAvailableHOC(
  SpeedAndAltitudeChart,
  ChartType.line,
  getChartOptions('ft', 'knots'),
  chartSeriesContent[ChartItemKeys.speedAndAltitude],
  'speedAndAltitudeSec',
);

const SpaceVehicleSpeedAndAltitudeChart = showChartOrDataAvailableHOC(
  withTerminalHistoryChartHOC(
    SpeedAndAltitudeChart,
    ChartType.line,
    getChartOptions('mi', 'mph'),
    spaceVehicleSpeedAndAltitudeSeriesContent,
    'speedAndAltitudeSec',
    CHART_HEIGHT.DEFAULT,
  ),
  spaceVehicleSpeedAndAltitudeSeriesContent,
  {
    selectTerminalHistory: selectSpaceVehicleChartTerminalHistory,
    selectOtherTerminalHistory: selectSpaceVehicleChartOtherTerminalHistory,
  },
);

const selectIsSpaceVehicle = (state: RootState) =>
  selectTailDetailsPanelTerminal(state)?.platformType === TerminalPlatformsEnum.SpaceVehicle;

const SpeedAndAltitudeChartForPlatform = (props: ComponentProps<typeof AirborneSpeedAndAltitudeChart>) => {
  const isSpaceVehicle = useSelector(selectIsSpaceVehicle);
  const hasSpaceVehicleOrbit = useSelector(selectHasSelectedSpaceVehicleOrbit);

  if (isSpaceVehicle) {
    // The chart plots its history only once. A new key mounts it again when the orbit arrives after the history.
    return <SpaceVehicleSpeedAndAltitudeChart key={String(hasSpaceVehicleOrbit)} {...props} />;
  }

  return <AirborneSpeedAndAltitudeChart {...props} />;
};

export default SpeedAndAltitudeChartForPlatform;
